/**
 * What the GUEST can decode in hardware, probed once and cached.
 *
 * The host picks the send codec from its own encoder capability alone, so a host with a
 * hardware AV1 encoder happily pins a phone that decodes AV1 in software. Fixing that needs
 * the guest's decode capability on the wire, and the guest cannot afford to discover it
 * during connect: `mediaCapabilities.decodingInfo` is an async round trip into the platform
 * decoder, and connect is the one path where added latency is visible to the player.
 *
 * So the probe runs ONCE, off the critical path, and connect reads the answer synchronously.
 * Before it resolves the answer is `undefined`, which callers publish as "field absent" —
 * absent means UNKNOWN and the host must behave exactly as it did before the field existed.
 * An empty array is different information: the guest asked and the platform said no.
 *
 * Relationship to `preferHardwareVideoCodecs`: that module answers "which of these codecs
 * should I rank first" and deliberately collapses its per-family verdicts into an ordering,
 * so it cannot report which families were hardware. This module asks the same
 * `mediaCapabilities.decodingInfo` question with the same `prefer-hardware` hint, and adds
 * the caching and the family-name projection — nothing else.
 */

import {
  DEFAULT_HW_PROBE_VIDEO,
  videoCodecFamily,
  type HardwareProbeVideoTarget,
} from './preferHardwareVideoCodecs';

/**
 * The one question this module asks, per codec family. Injectable so the cache can be
 * tested without a browser, and so a caller can supply a probe of its own.
 */
export type DecodeCapabilityProbe = (
  contentType: string,
) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;

/** Families worth asking about. Anything WebRTC video negotiation can land on. */
const DEFAULT_DECODE_FAMILIES = ['h264', 'h265', 'av1', 'vp8', 'vp9'] as const;

/**
 * Families whose hardware decode is SHIPPED but whose probe reports "no".
 *
 * Safari/WebKit is the known case. VideoToolbox decodes HEVC in hardware on every
 * Apple Silicon / iPhone / iPad that ships, and the guest can offer H.265 — but
 * `decodingInfo` on WebKit is incomplete for HEVC and answers unsupported /
 * software even when hardware decode exists. If that negative answer reached the
 * host's send-codec pin, the host would exclude H.265 — the ONLY hardware escape a
 * Safari guest has (its H.264 offer is constrained-baseline-only,
 * software-encodable on the host fleet) — and the whole hardware-hardware HEVC path
 * would be vetoed by a probe the platform cannot answer truthfully.
 *
 * A negative probe is therefore not trusted for these families; a positive one
 * still wins. The families stay listed so a future WebKit fix makes the platform
 * answer authoritative again.
 */
const WEBSHIM_HARDWARE_DECODE_FAMILIES = new Set(['h265']);

function isWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent;
  // Safari and AppleWebKit identify themselves; Chrome/Chromium also contain
  // "AppleWebKit" but not "Safari" (Chrome's UA names "Chrome" instead), which is
  // exactly the distinction the app's own isSafari() makes.
  return userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Chromium');
}

/** Representative RTP mime types, so each family is asked about in its own terms. */
const FAMILY_MIME_TYPE: Record<string, string> = {
  h264: 'video/H264',
  h265: 'video/H265',
  av1: 'video/AV1',
  vp8: 'video/VP8',
  vp9: 'video/VP9',
};

/**
 * How long the whole probe may take before it is abandoned as unanswerable.
 *
 * A platform that never answers is indistinguishable from one that cannot, and holding an
 * in-flight promise forever would keep every later caller waiting on it. Well clear of a
 * real decodingInfo call, which is a local query.
 */
const PROBE_BUDGET_MS = 2_000;

let cachedFamilies: string[] | undefined;
let inFlightProbe: Promise<string[]> | null = null;
/** Bumped by reset() so a probe still in flight cannot write into the cleared cache. */
let cacheGeneration = 0;

function contentTypeFor(family: string): string {
  const mimeType = FAMILY_MIME_TYPE[family] ?? `video/${family}`;
  return mimeType.includes('/') ? mimeType : `video/${mimeType}`;
}

/** Normalizes caller-supplied names ('hevc', 'AVC') onto the family vocabulary, deduped. */
function normalizeFamilies(families: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const family of families) {
    const key = videoCodecFamily(family);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }
  return normalized;
}

/** `mediaCapabilities.decodingInfo`, adapted to the injectable probe shape. */
function browserDecodeProbe(
  video: HardwareProbeVideoTarget = DEFAULT_HW_PROBE_VIDEO,
): DecodeCapabilityProbe | null {
  const mediaCapabilities = globalThis.navigator?.mediaCapabilities;
  const decodingInfo = mediaCapabilities?.decodingInfo;
  if (!decodingInfo) return null;

  return (contentType: string) =>
    decodingInfo.call(mediaCapabilities, {
      type: 'webrtc',
      video: {
        contentType,
        width: video.width,
        height: video.height,
        bitrate: video.bitrate,
        framerate: video.framerate,
      },
      // Non-standard Chromium hint, the same one preferHardwareVideoCodecs relies on: it is
      // what makes Chromium answer for the hardware path. Ignored elsewhere.
      hardwareAcceleration: 'prefer-hardware',
    } as MediaDecodingConfiguration);
}

async function probeFamilies(
  probe: DecodeCapabilityProbe,
  families: string[],
): Promise<string[]> {
  const verdicts = await Promise.all(
    families.map(async (family) => {
      try {
        const info = await probe(contentTypeFor(family));
        const probeSaysHardware = info?.supported === true && info?.powerEfficient === true;
        if (probeSaysHardware) return true;
        // Negative probe answers are untrusted on WebKit for families whose hardware
        // decode ships (VideoToolbox HEVC) but whose decodingInfo cannot report it.
        return isWebKit() && WEBSHIM_HARDWARE_DECODE_FAMILIES.has(family);
      } catch {
        return isWebKit() && WEBSHIM_HARDWARE_DECODE_FAMILIES.has(family);
      }
    }),
  );
  return families.filter((_, index) => verdicts[index]);
}

async function runBoundedProbe(
  probe: DecodeCapabilityProbe | null,
  families: string[],
): Promise<string[]> {
  if (!probe || families.length === 0) return [];

  let budget: ReturnType<typeof setTimeout> | undefined;
  const abandonUnanswered = new Promise<string[]>((resolve) => {
    budget = setTimeout(() => resolve([]), PROBE_BUDGET_MS);
  });

  try {
    return await Promise.race([probeFamilies(probe, families), abandonUnanswered]);
  } catch {
    return [];
  } finally {
    if (budget) clearTimeout(budget);
  }
}

/**
 * Start the probe. Idempotent and safe to call more than once; the first call wins and
 * later calls return the same promise. Never throws; resolves to [] when capability
 * cannot be determined, which callers must treat as "unknown", not "none".
 */
export function primeGuestDecodeCapability(options?: {
  probe?: DecodeCapabilityProbe | null;
  families?: string[];
}): Promise<string[]> {
  if (cachedFamilies !== undefined) return Promise.resolve(cachedFamilies);
  if (inFlightProbe) return inFlightProbe;

  const probe = options && 'probe' in options ? (options.probe ?? null) : browserDecodeProbe();
  const families = normalizeFamilies(options?.families ?? DEFAULT_DECODE_FAMILIES);

  const generation = cacheGeneration;
  inFlightProbe = runBoundedProbe(probe, families).then((resolved) => {
    if (generation !== cacheGeneration) return resolved;
    cachedFamilies = resolved;
    inFlightProbe = null;
    return resolved;
  });
  return inFlightProbe;
}

/**
 * Synchronous read for callers on the connect critical path. Returns undefined until the
 * probe has resolved — callers MUST omit the metadata field in that case rather than
 * sending an empty array, because [] and undefined mean different things to the host.
 */
export function getGuestDecodeCapability(): string[] | undefined {
  return cachedFamilies;
}

/** Test seam. Clears the cache and any in-flight probe. */
export function resetGuestDecodeCapability(): void {
  cacheGeneration += 1;
  cachedFamilies = undefined;
  inFlightProbe = null;
}
